import { Component, EventEmitter, Output, computed, input } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { RecipeFacetsCatalogue } from '../../services/recipe/recipe-facets.models';

/**
 * Classification editor for the manage-recipe form: course (single-select),
 * seasons and dish types (multi-select). Renders every option as a selectable
 * pill so the whole vocabulary is visible at a glance and toggling is one tap.
 *
 * UI chrome (labels, hint, states) is English; the option data itself stays in
 * the catalogue's own language (German) — the component never rewrites it. Pill
 * colours mirror the read-only facet badges: course=primary, season=info,
 * dishType=neutral, so classification reads the same across list, filter and
 * edit surfaces.
 *
 * Selection state is owned by the parent; this component only emits toggles.
 */
@Component({
  selector: 'app-recipe-facet-editor',
  standalone: true,
  imports: [FontAwesomeModule],
  template: `
    <p class="facet-hint">
      These tags are detected automatically. Editing them here overrides and locks
      the automatic classification.
      @if (userEdited()) {
        <span class="facet-hint__flag">Currently set manually.</span>
      }
    </p>

    @if (catalogue(); as cat) {
      <!-- Course — single-select -->
      <div class="facet-group">
        <span class="facet-group__label">Course</span>
        <div class="facet-pills">
          @for (opt of cat.courses; track opt.key) {
            <button
              type="button"
              class="pill pill--course"
              [class.is-on]="course() === opt.key"
              [attr.aria-pressed]="course() === opt.key"
              [disabled]="disabled()"
              (click)="courseChange.emit(course() === opt.key ? '' : opt.key)">
              <fa-icon class="pill__check" [icon]="faCheck"></fa-icon>
              <span>{{ opt.label }}</span>
            </button>
          }
        </div>
      </div>

      <!-- Seasons — multi-select -->
      <div class="facet-group">
        <span class="facet-group__label">Season</span>
        <div class="facet-pills">
          @for (opt of cat.seasons; track opt.key) {
            <button
              type="button"
              class="pill pill--season"
              [class.is-on]="seasons().has(opt.key)"
              [attr.aria-pressed]="seasons().has(opt.key)"
              [disabled]="disabled()"
              (click)="seasonToggle.emit(opt.key)">
              <fa-icon class="pill__check" [icon]="faCheck"></fa-icon>
              <span>{{ opt.label }}</span>
            </button>
          }
        </div>
      </div>

      <!-- Dish types — multi-select (admin vocabulary) -->
      <div class="facet-group facet-group--last">
        <span class="facet-group__label">Dish type</span>
        @if (cat.dish_types.length > 0) {
          <div class="facet-pills">
            @for (opt of cat.dish_types; track opt.key) {
              <button
                type="button"
                class="pill pill--dishtype"
                [class.is-on]="dishTypes().has(opt.key)"
                [attr.aria-pressed]="dishTypes().has(opt.key)"
                [disabled]="disabled()"
                (click)="dishTypeToggle.emit(opt.key)">
                <fa-icon class="pill__check" [icon]="faCheck"></fa-icon>
                <span>{{ dishTypeLabel(opt) }}</span>
              </button>
            }
          </div>
        } @else {
          <p class="facet-empty">No dish-type tags available yet.</p>
        }
      </div>
    } @else {
      <p class="facet-empty">Loading classification…</p>
    }
  `,
  styles: [`
    :host { display: block; }

    .facet-hint {
      font-size: 0.8125rem;
      color: color-mix(in oklch, var(--color-base-content) 60%, transparent);
      margin: 0 0 1rem;
    }
    .facet-hint__flag {
      font-style: italic;
      color: color-mix(in oklch, var(--color-base-content) 75%, transparent);
    }

    .facet-group { margin-bottom: 1.25rem; }
    .facet-group--last { margin-bottom: 0; }
    .facet-group__label {
      display: block;
      font-size: 0.6875rem;
      letter-spacing: .08em;
      text-transform: uppercase;
      font-weight: 700;
      color: color-mix(in oklch, var(--color-base-content) 55%, transparent);
      margin-bottom: 0.5rem;
    }

    .facet-pills { display: flex; flex-wrap: wrap; gap: 0.5rem; }

    .pill {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      border: 1px solid var(--color-base-300);
      background: var(--color-base-100);
      color: color-mix(in oklch, var(--color-base-content) 78%, transparent);
      border-radius: 999px;
      padding: 0.35rem 0.85rem;
      font-size: 0.84rem;
      font-weight: 600;
      line-height: 1.2;
      cursor: pointer;
      transition: border-color .12s, background-color .12s, color .12s;
    }
    .pill:hover:not(:disabled):not(.is-on) {
      border-color: color-mix(in oklch, var(--color-base-content) 30%, transparent);
      color: var(--color-base-content);
    }
    .pill:disabled { opacity: .55; cursor: not-allowed; }

    /* The check only takes up space once the pill is active — keeps idle pills tight. */
    .pill__check { width: 0; overflow: hidden; opacity: 0; font-size: 0.7rem; transition: width .12s, opacity .12s; }
    .pill.is-on .pill__check { width: 0.7rem; opacity: 1; }

    .pill--course.is-on {
      background: var(--color-primary);
      border-color: var(--color-primary);
      color: var(--color-primary-content);
    }
    .pill--season.is-on {
      background: color-mix(in oklch, var(--color-info) 16%, transparent);
      border-color: color-mix(in oklch, var(--color-info) 55%, transparent);
      color: var(--color-info);
    }
    .pill--dishtype.is-on {
      background: color-mix(in oklch, var(--color-base-content) 12%, transparent);
      border-color: color-mix(in oklch, var(--color-base-content) 35%, transparent);
      color: var(--color-base-content);
    }

    .pill:focus-visible {
      outline: 2px solid var(--color-accent);
      outline-offset: 2px;
    }

    .facet-empty {
      font-size: 0.8125rem;
      color: color-mix(in oklch, var(--color-base-content) 60%, transparent);
      margin: 0;
    }
  `],
})
export class RecipeFacetEditorComponent {
  readonly faCheck = faCheck;

  readonly catalogue = input<RecipeFacetsCatalogue | null>(null);
  readonly course = input('', { transform: (v: string | null | undefined) => v ?? '' });
  readonly seasons = input<ReadonlySet<string>>(new Set<string>());
  readonly dishTypes = input<ReadonlySet<string>>(new Set<string>());
  readonly userEdited = input(false, { transform: (v: boolean | null | undefined) => !!v });
  readonly disabled = input(false, { transform: (v: boolean | null | undefined) => !!v });

  /** Emits the new course key ('' when the active course is toggled off). */
  @Output() courseChange = new EventEmitter<string>();
  /** Emits the season key to flip; parent owns the add/remove. */
  @Output() seasonToggle = new EventEmitter<string>();
  /** Emits the dish-type key to flip; parent owns the add/remove. */
  @Output() dishTypeToggle = new EventEmitter<string>();

  dishTypeLabel(opt: { key: string; name_de: string; name_en: string }): string {
    return opt.name_de || opt.name_en || opt.key;
  }

  // Keeps the template referencing a stable member rather than re-reading input.
  readonly hasCatalogue = computed(() => !!this.catalogue());
}
